import { STORAGE_KEYS } from "@/constants";

/**
 * The session lives in localStorage, not Vuex, because several of these values have to
 * survive a hard reload (the 401 interceptor and the hand-back both depend on that).
 *
 * A plain object of functions, not a class — nothing here holds state worth instantiating.
 */

const read = (key) => localStorage.getItem(key);
const write = (key, value) => {
  if (value === null || value === undefined) localStorage.removeItem(key);
  else localStorage.setItem(key, String(value));
};

export const session = {
  accessToken: () => read(STORAGE_KEYS.accessToken),
  trackingId: () => read(STORAGE_KEYS.id),
  inboundToken: () => read(STORAGE_KEYS.token),
  returnUrl: () => read(STORAGE_KEYS.returnUrl),
  /** JWT `exp`, in epoch seconds, driving the countdown. */
  expiresAt: () => Number(read(STORAGE_KEYS.timer)) || 0,

  /** Presence of an access token is what "logged in" means here — no signature check. */
  isAuthenticated: () => read(STORAGE_KEYS.accessToken) != null,

  start({ accessToken, id, exp, token, returnUrl }) {
    write(STORAGE_KEYS.accessToken, accessToken);
    write(STORAGE_KEYS.id, id);
    write(STORAGE_KEYS.timer, exp);
    write(STORAGE_KEYS.token, token);
    write(STORAGE_KEYS.returnUrl, returnUrl);
  },

  /**
   * Called once the flow is submitted: the credential and the countdown are spent, but
   * the tracking id, inbound token and return URL are still needed to render the done
   * screen and hand back to the referring system.
   */
  endAuth() {
    write(STORAGE_KEYS.accessToken, null);
    write(STORAGE_KEYS.timer, null);
  },

  clear() {
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  },
};

/**
 * Decode a JWT payload without verifying it.
 *
 * Only `exp` is read, and only to drive a client-side countdown — the backend is the
 * actual authority on expiry, so there is nothing to verify here. Inlined rather than
 * pulling in `vue-jwt-decode`, which is unmaintained and wraps exactly this.
 */
export function decodeJwt(token) {
  try {
    const payload = String(token).split(".")[1];
    if (!payload) return null;
    const normalised = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape(window.atob(normalised))));
  } catch (e) {
    return null;
  }
}
